import React, { useEffect } from 'react'
import {
	makeStyles,
	Card,
	CardHeader,
	CardContent,
	Checkbox,
	FormControlLabel,
	Typography,
	TextField,
	MenuItem,
	ListItemText
} from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'

import { getMarkets } from '../../../actions/markets/markets.actions'
import { getFacilities } from '../../../actions/facilities/facilities.actions'
import { getApartments } from '../../../actions/general.actions'
import { states } from '../../../services/helpers'
import clsx from 'clsx'
import Autocomplete from '@material-ui/lab/Autocomplete'
import useConstants from '../../../hooks/useConstants'

const useStyles = makeStyles((theme) => ({
	grid: {
		display: 'grid',
		gridGap: '23px',
		gridTemplateColumns: 'repeat(auto-fill, minmax(274px, 1fr))',
		marginBottom: 36
	},
	gridDouble: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(424px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '424px !important'
			}
		}
	},
	fieldWrapper: {
		'& .MuiTextField-root': {
			minWidth: '274px !important'
		}
	},
	autoComplete: {
		'& .MuiFilledInput-root': {
			paddingTop: '17px'
		},
		'& .MuiInputLabel-filled': {
			transform: 'translate(12px, 20px) scale(1)'
		},
		'& .Mui-focused': {
			'& .MuiAutocomplete-endAdornment svg': {
				fill: theme.palette.secondary.main
			}
		},
		'& .MuiAutocomplete-endAdornment svg': {
			fill: '#777679'
		}
	}
}))

const Location = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const markets = useSelector((state) => state.specific.markets.markets)
	const facilities = useSelector((state) => state.specific.facilities.facilities)
	const apartments = useSelector((state) => state.general.apartments.apartments)
	const facilitiesLoading = useSelector((state) => state.specific.facilities.loading)
	const apartmentsLoading = useSelector((state) => state.general.apartments.loading)
	const { buildingTypes } = useConstants()

	let { household, setHousehold } = props
	let { contact } = household
	let { address } = contact
	let { loc } = address

	let dogWalkingOptions = [
		{ name: 'In Area', value: 'in_area' },
		{ name: 'Wait List', value: 'wait_list' },
		{ name: 'Out of Area', value: 'out_of_area' }
	]

	useEffect(() => {
		if (!markets) {
			dispatch(getMarkets())
		}

		if (!facilities) {
			dispatch(getFacilities())
		}

		if (!apartments && household) {
			dispatch(getApartments(household.market_id))
		}
	}, [markets, facilities, apartments])

	const skiptownFacilities = (facilities || [])
		.filter((facility) => facility.skiptown)
		.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
	const selectedFacilityId = skiptownFacilities.some(
		(facility) => String(facility._id) === String(household.default_facility_id)
	)
		? String(household.default_facility_id)
		: ''

	const updateHousehold = (field, value) => {
		if (household) {
			setHousehold({ ...household, [field]: value })
		}
	}

	const handleFacilityChange = (facilityId) => {
		const facility = skiptownFacilities.find((item) => String(item._id) === String(facilityId))
		const marketId = facility?.market_id
		const market = (markets || []).find((item) => String(item._id) === String(marketId)) || null

		setHousehold({
			...household,
			default_facility_id: facilityId || null,
			market: market || household.market,
			market_id: marketId || household.market_id
		})

		if (marketId && String(marketId) !== String(household.market_id)) {
			dispatch(getApartments(marketId))
		}
	}

	const updateHouseholdContact = (field, value) => {
		if (!address) {
			address = {
				address_line_1: '',
				address_line_2: '',
				city: '',
				state: '',
				zip_code: '',
				loc: {
					coordinates: []
				},
				override_geocode: false,
				enforce_device_within_region: false
			}
		}

		address = { ...address, [field]: value }

		updateHousehold('contact', { ...contact, address })
	}

	return (
		<Card className={classes.card}>
			<CardHeader title="Location" />
			<CardContent>
				<div>
					<div className={clsx([classes.grid, classes.gridDouble])} style={{ marginBottom: 36 }}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								variant="filled"
								select
								value={selectedFacilityId}
								label="Default Facility"
								disabled={facilitiesLoading}
								onChange={(e) => handleFacilityChange(e.target.value)}>
								<MenuItem value="">
									{skiptownFacilities.length > 0 ? 'Select a facility' : 'No Facilities'}
								</MenuItem>
								{skiptownFacilities.map((facility) => (
									<MenuItem key={facility._id} value={String(facility._id)}>
										{facility.market?.city
											? `${facility.name} (${facility.market.city})`
											: facility.name}
									</MenuItem>
								))}
							</TextField>
						</div>
					</div>
					<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
						Address
					</Typography>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								label="Address 1"
								variant="filled"
								value={address.address_line_1 || ''}
								onChange={(e) => updateHouseholdContact('address_line_1', e.target.value)}
							/>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								label="Address 2"
								variant="filled"
								value={address.address_line_2 || ''}
								onChange={(e) => updateHouseholdContact('address_line_2', e.target.value)}
							/>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								label="City"
								variant="filled"
								value={address.city || ''}
								onChange={(e) => updateHouseholdContact('city', e.target.value)}
							/>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Autocomplete
								size="small"
								getOptionLabel={(option) => option.name}
								id="household-select"
								className={classes.autoComplete}
								options={states || []}
								value={address.state ? states.find((s) => s.id === address.state.id) : ''}
								onChange={(e, v) => updateHouseholdContact('state', v)}
								onClose={(e) => {
									e.target.value = ''
								}}
								renderOption={(option) => (
									<React.Fragment>
										<ListItemText primary={option.name} />
									</React.Fragment>
								)}
								renderInput={(params) => <TextField {...params} variant="filled" label="State" />}
								onKeyPress={(e, v) =>
									e && e.key === 'Enter' ? updateHouseholdContact('state', v) : null
								}
							/>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								label="Zip"
								variant="filled"
								value={address.zip_code || ''}
								onChange={(e) => updateHouseholdContact('zip_code', e.target.value)}
							/>
						</div>
					</div>
				</div>
				<div>
					<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
						Geocode
					</Typography>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								variant="filled"
								select
								value={address.enforce_device_within_region}
								label="Enforce Geofence?"
								onChange={(e) =>
									updateHouseholdContact('enforce_device_within_region', e.target.value)
								}>
								<MenuItem value={true}>True</MenuItem>
								<MenuItem value={false}>False</MenuItem>
							</TextField>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								label="Latitude"
								variant="filled"
								disabled
								value={
									loc && loc.coordinates && loc.coordinates.length === 2
										? loc.coordinates[1]
										: loc.latitude
										? loc.latitude
										: ''
								}
							/>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								label="Longitude"
								variant="filled"
								disabled
								value={
									loc && loc.coordinates && loc.coordinates.length === 2
										? loc.coordinates[0]
										: loc.longitude
										? loc.longitude
										: ''
								}
							/>
						</div>
						<div>
							<FormControlLabel
								control={
									<Checkbox
										checked={address.override_geocode}
										onChange={(e) =>
											updateHouseholdContact('override_geocode', e.target.checked)
										}
									/>
								}
								label="Override Geocode"
							/>
						</div>
					</div>
				</div>
				<div>
					<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
						Building Type
					</Typography>
					<div style={{ marginBottom: 0 }} className={clsx([classes.grid, classes.gridDouble])}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								label="Building Type"
								variant="filled"
								select
								disabled={buildingTypes.length < 1}
								value={
									buildingTypes.length && household.building_type
										? buildingTypes.find(
												(bT) =>
													bT.name === household.building_type ||
													bT.name.includes(household.building_type)
										  )
										: ''
								}
								onChange={(e) => updateHousehold('building_type', e.target.value.name)}>
								{buildingTypes.length ? (
									buildingTypes.map((buildingType) => (
										<MenuItem key={buildingType.id} value={buildingType}>
											{buildingType.name}
										</MenuItem>
									))
								) : (
									<MenuItem>No Building Types</MenuItem>
								)}
							</TextField>
						</div>
						{household &&
						household.building_type &&
						(household.building_type.includes('Apartment') ||
							household.building_type.includes('Condo')) ? (
							<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
								<TextField
									label="Apartment"
									variant="filled"
									select
									disabled={apartmentsLoading}
									value={
										household &&
										household.apartment &&
										household.apartment._id &&
										apartments &&
										apartments.length
											? apartments.find((a) => a._id === household.apartment._id)
											: ''
									}
									onChange={(e) => updateHousehold('apartment', e.target.value)}>
									{apartments && apartments.length > 0 ? (
										apartments.map((apartment) => (
											<MenuItem key={apartment._id} value={apartment}>
												{apartment.name}
											</MenuItem>
										))
									) : (
										<MenuItem>No Apartments</MenuItem>
									)}
								</TextField>
							</div>
						) : null}
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Autocomplete
								size="small"
								getOptionLabel={(option) => option.name}
								id="dog-walking-select"
								className={classes.autoComplete}
								options={dogWalkingOptions || []}
								value={
									household.dog_walking
										? dogWalkingOptions.find((o) => household.dog_walking === o.value)
										: ''
								}
								onChange={(e, v) =>
									setHousehold({ ...household, dog_walking: v ? v.value : null })
								}
								onClose={(e) => {
									e.target.value = ''
								}}
								renderOption={(option) => (
									<React.Fragment>
										<ListItemText primary={option.name} />
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField {...params} variant="filled" label="Dog Walking" />
								)}
								onKeyPress={(e, v) =>
									e && e.key === 'Enter'
										? setHousehold({ ...household, dog_walking: v ? v.value : null })
										: null
								}
							/>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	)
}

export default Location
